import React from "react";
import { Card, Spin, Tabs, Result, Space, Button } from "antd";
import { FormattedMessage } from "react-intl";
import { useSearchParams, useNavigate, useParams } from "react-router-dom";
import { LoadingOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import styled from "styled-components";

import NodeAnalyticsTitle from "./NodeAnalyticsTitle";
import useNodeChannels from "../../hooks/useNodeChannels";
import useSetNodeConnection from "../../hooks/useSetNodeConnection";
import useNodeCapabilities from "../../hooks/useNodeCapabilities";
import VMAF from "./VMAF";

import { selectors as NODE_SELECTORS } from "../../ducks/node";
import { selectors as NODE_CHANNELS_SELECTOR } from "../../ducks/nodeChannels";

import NodeAnalyticsResults from "./NodeAnalyticsResults";

const NodeAnalytics = () => {
  useSetNodeConnection();
  useNodeChannels();
  useNodeCapabilities();
  const [searchParams, setSearchParams] = useSearchParams();
  const channelId = searchParams.get("c");
  const outputId = searchParams.get("o");
  const nodeAnalytics = useSelector(NODE_SELECTORS.getNodeAnalytics);
  const isTestStarted = useSelector(NODE_SELECTORS.getNodeTestStarted);
  const navigate = useNavigate();
  const tab = searchParams.get("tabt") || (nodeAnalytics && nodeAnalytics.length > 0 ? nodeAnalytics[0] : "");
  const { cwid, cnn } = useParams();

  const isChannelStarted = useSelector((state) => NODE_CHANNELS_SELECTOR.getIsChannelStarted({ state, channelId }));
  const tabItems = [
    {
      key: "VMAF",
      label: "VMAF",
      children: <VMAF />,
    },
  ];

  const filteredTabItems = tabItems.filter((item) => nodeAnalytics && nodeAnalytics.includes(item.key)) || [];

  const handleChangeTab = (selectedTab) => {
    searchParams.set("tabt", selectedTab);
    setSearchParams(searchParams);
  };

  return (
    <Card title={<NodeAnalyticsTitle />}>
      <Space direction="vertical" size="large" style={{ display: "flex" }}>
        <Card title={<FormattedMessage id="NodeAnalytics.testConfiguration" defaultMessage="Test configuration" />}>
          {channelId && outputId && !isChannelStarted && (
            <Result
              status="info"
              title={
                <StyledSpan>
                  <FormattedMessage
                    id="NodeAnalytics.selectedChannelNeedsToBeStarted"
                    defaultMessage="Selected channel needs to be started"
                  />
                </StyledSpan>
              }
              extra={
                <Button type="primary" onClick={() => navigate(`/node/${cwid}/${cnn}/channel/${channelId}`)}>
                  <FormattedMessage id="NodeAnalytics.channelDetails" defaultMessage="Channel details" />
                </Button>
              }
              icon={<InfoCircleOutlined style={{ fontSize: 36 }} />}
            />
          )}
          {(!channelId || !outputId) && (
            <Result
              status="info"
              title={
                <StyledSpan>
                  <FormattedMessage
                    id="NodeAnalytics.pleaseSelectChannelAndOutput"
                    defaultMessage="Please select channel and output"
                  />
                </StyledSpan>
              }
              icon={<InfoCircleOutlined style={{ fontSize: 36 }} />}
            />
          )}
          {channelId && outputId && isChannelStarted && (
            <Spin
              spinning={isTestStarted}
              tip={<FormattedMessage id="NodeAnalytics.testInProgress" defaultMessage="Test in progress" />}
              indicator={<LoadingOutlined />}
            >
              <Tabs items={filteredTabItems} onChange={handleChangeTab} activeKey={tab} />
            </Spin>
          )}
        </Card>
        <NodeAnalyticsResults />
      </Space>
    </Card>
  );
};

const StyledSpan = styled.span`
  font-size: 16px;
`;

export default NodeAnalytics;
